/**
 * Onboarding step model (APP-002 §5).
 *
 * Defines the canonical step sequence and labels. The flow is deliberately
 * short (APP-002 §5: "Avoid more than three active screens unless the backend
 * requires additional setup"):
 *
 *   welcome → account_setup (optional) → product_orientation → next_action
 *
 * The account-setup step is included only when the backend supports editing
 * the account name (it does — PATCH /v1/dashboard/account). It is skippable.
 */
export type OnboardingStepId = 'welcome' | 'account_setup' | 'orientation' | 'next_action';

export interface OnboardingStep {
  id: OnboardingStepId;
  /** Human-readable heading for the step. */
  title: string;
}

/**
 * The canonical ordered step list. Progress UI renders this as a semantic
 * ordered list (APP-002 §26), never relying on visual dots alone.
 */
export const ONBOARDING_STEPS: readonly OnboardingStep[] = [
  { id: 'welcome', title: 'Welcome' },
  { id: 'account_setup', title: 'Account setup' },
  { id: 'orientation', title: 'How it works' },
  { id: 'next_action', title: 'Get started' },
] as const;

export const ONBOARDING_TOTAL_STEPS = ONBOARDING_STEPS.length;

/**
 * Return the 1-based ordinal for a step id (for progress announcement), or 0
 * if unknown.
 */
export function stepOrdinal(id: OnboardingStepId): number {
  const idx = ONBOARDING_STEPS.findIndex((s) => s.id === id);
  return idx >= 0 ? idx + 1 : 0;
}

/** The next step id, or null if this is the last step. */
export function nextStepId(id: OnboardingStepId): OnboardingStepId | null {
  const idx = ONBOARDING_STEPS.findIndex((s) => s.id === id);
  if (idx < 0 || idx >= ONBOARDING_STEPS.length - 1) return null;
  return ONBOARDING_STEPS[idx + 1].id;
}

/** The previous step id, or null if this is the first step. */
export function previousStepId(id: OnboardingStepId): OnboardingStepId | null {
  const idx = ONBOARDING_STEPS.findIndex((s) => s.id === id);
  if (idx <= 0) return null;
  return ONBOARDING_STEPS[idx - 1].id;
}
